import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, useScroll, useSpring } from 'framer-motion';
import { API } from '../config';

// Import New Components
import ModernHero from '../components/home/ModernHero';
import StatsSection from '../components/home/StatsSection';
import ServicesSection from '../components/home/ServicesSection';
import HomeAbout from '../components/home/HomeAbout';
import ImpactSection from '../components/home/ImpactSection';
import WhyChooseSection from '../components/home/WhyChooseSection';
import Footer from '../components/Footer';

const HomePage = () => {
    const [heroData, setHeroData] = useState(null);
    const [stats, setStats] = useState([]);
    const [services, setServices] = useState([]);
    const [features, setFeatures] = useState([]);
    const [siteContent, setSiteContent] = useState([]);
    const [loading, setLoading] = useState(true);

    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    useEffect(() => {
        const fetchAllData = async () => {
            try {
                const [heroRes, statsRes, servicesRes, featuresRes, contentRes] = await Promise.all([
                    axios.get(`${API}/hero-images?active=true`),
                    axios.get(`${API}/stats`),
                    axios.get(`${API}/services`),
                    axios.get(`${API}/features`),
                    axios.get(`${API}/site-content`)
                ]);

                // Set hero data (first active)
                if (heroRes.data && heroRes.data.length > 0) {
                    setHeroData(heroRes.data[0]);
                }

                setStats(statsRes.data || []);
                setServices(servicesRes.data || []);
                setFeatures(featuresRes.data || []);
                setSiteContent(contentRes.data || []);

                setLoading(false);
            } catch (error) {
                console.error("Error fetching homepage data:", error);
                setLoading(false);
            }
        };

        fetchAllData();
    }, []);

    // Scroll to section handler
    useEffect(() => {
        const scrollTo = window.history.state?.usr?.scrollTo;
        if (scrollTo) {
            const id = scrollTo.replace('#', '');
            setTimeout(() => {
                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
            }, 500);
        }
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0a221a] flex items-center justify-center">
                <div className="relative">
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                        className="w-20 h-20 border-2 border-agro-lime/20 border-t-agro-lime rounded-full"
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-2 h-2 bg-agro-lime rounded-full animate-pulse"></div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <main className="bg-white selection:bg-agro-lime selection:text-agro-green-dark">
            {/* Scroll Progress Bar */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-agro-lime z-[100] origin-left"
                style={{ scaleX }}
            />

            {/* Hero Section */}
            <ModernHero data={heroData} />

            <div className="relative">
                {/* Stats Section */}
                <StatsSection stats={stats} />

                {/* About & Mission Section */}
                <div id="about" className="scroll-mt-20">
                    <HomeAbout content={siteContent} />
                </div>

                {/* Services Section */}
                <div id="services" className="scroll-mt-20">
                    <ServicesSection services={services} />
                </div>

                {/* Impact Section */}
                <div id="impact" className="scroll-mt-20">
                    <ImpactSection content={siteContent} />
                </div>

                {/* Why Choose Us */}
                <div id="why-choose" className="scroll-mt-20">
                    <WhyChooseSection features={features} />
                </div>

                {/* Final Call to Action */}
                <section id="contact" className="py-24 bg-white scroll-mt-20">
                    <div className="container mx-auto px-6">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="bg-[#0a221a] rounded-[4rem] overflow-hidden flex flex-col lg:flex-row shadow-[0_50px_100px_-20px_rgba(10,34,26,0.3)] border border-white/5"
                        >
                            <div className="lg:w-1/2 p-12 md:p-24 flex flex-col justify-center relative">
                                <div className="absolute top-10 left-10 w-32 h-32 bg-agro-lime/5 rounded-full blur-3xl"></div>
                                <h2 className="text-4xl md:text-6xl font-serif text-white mb-8 leading-tight">Empowering Rural <br /><span className="text-agro-lime">Karnataka</span></h2>
                                <p className="text-agro-cream/60 mb-12 text-xl leading-relaxed font-light">
                                    Join hands with SR Agro Farms. Whether you're a farmer looking for contract poultry
                                    or a business seeking quality produce, we're here to grow together.
                                </p>
                                <div>
                                    <button
                                        onClick={() => window.open('https://wa.me/919845000069', '_blank')}
                                        className="bg-agro-lime text-agro-green-dark px-12 py-5 rounded-2xl font-bold uppercase tracking-[0.2em] text-xs hover:bg-white transition-all transform hover:scale-105 active:scale-95 shadow-xl shadow-agro-lime/10"
                                    >
                                        Join us in building a Nutrition India
                                    </button>
                                </div>
                            </div>
                            <div className="lg:w-1/2 h-96 lg:h-auto relative overflow-hidden">
                                <motion.img
                                    initial={{ scale: 1.2 }}
                                    whileInView={{ scale: 1 }}
                                    transition={{ duration: 1.5 }}
                                    src="/images/sr-agro-3.jpeg"
                                    alt="Aerial Farm View"
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-r from-[#0a221a] via-transparent to-transparent"></div>
                            </div>

                        </motion.div>
                    </div>
                </section>

                <Footer />
            </div>
        </main>
    );
};

export default HomePage;
